import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { csAdminApi } from '../../services/api';
import { getCsAdminRoleLabel } from '../../constants/csAdminRoles';

const DEFAULT_PERMISSIONS = {
  companies: { view: true, edit: true, create: false, delete: false, export: true },
  directors: { view: true, edit: true, create: true, delete: false, export: true },
  committees: { view: true, edit: true, create: true, delete: true, export: true },
  orgStructure: { view: true, edit: false, exportPng: true, exportSvg: true, print: true },
  expiryAlerts: { view: true, configure: false, export: true },
  settings: { view: true, edit: true },
};

const MODULES = [
  { key: 'companies', label: 'Companies', actions: ['view', 'edit', 'create', 'delete', 'export'] },
  { key: 'directors', label: 'Directors', actions: ['view', 'edit', 'create', 'delete', 'export'] },
  { key: 'committees', label: 'Committees', actions: ['view', 'edit', 'create', 'delete', 'export'] },
  { key: 'orgStructure', label: 'Org Structure', actions: ['view', 'edit', 'exportPng', 'exportSvg', 'print'] },
  { key: 'expiryAlerts', label: 'Expiry Alerts', actions: ['view', 'configure', 'export'] },
  { key: 'settings', label: 'Settings', actions: ['view', 'edit'] },
];

const ACTION_LABELS = {
  view: 'View',
  edit: 'Edit',
  create: 'Create',
  delete: 'Delete',
  export: 'Export',
  exportPng: 'PNG',
  exportSvg: 'SVG',
  print: 'Print',
  configure: 'Configure',
};

const EditGlobalPermissionsDialog = ({
  open,
  principal,
  principalType = 'user',
  onClose,
  onSave,
}) => {
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);

  useEffect(() => {
    if (!open || !principal) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = principalType === 'portal_account'
          ? await csAdminApi.getPortalAccountPermissions(principal.id)
          : await csAdminApi.getUserPermissions(principal.id);
        const data = res.data?.data;
        if (cancelled || !data) return;
        setUseCustom(!!data.useCustom);
        setPermissions(data.useCustom ? (data.matrix || data.effective) : (data.effective || data.matrix));
      } catch (err) {
        console.error('Load permissions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, principal, principalType]);

  const handlePermissionChange = (module, action, value) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: { ...prev[module], [action]: value },
    }));
  };

  if (!principal) return null;

  const roleLabel = principal.roleLabel || getCsAdminRoleLabel(principal.role);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Permissions - {principal.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Using default permissions for: <strong>{roleLabel}</strong>
              </Alert>

              <FormControlLabel
                control={(
                  <Switch
                    checked={useCustom}
                    onChange={(e) => setUseCustom(e.target.checked)}
                  />
                )}
                label="Use custom permissions (override role defaults)"
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                MODULE PERMISSIONS
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell align="center">View</TableCell>
                      <TableCell align="center">Edit</TableCell>
                      <TableCell align="center">Create</TableCell>
                      <TableCell align="center">Delete</TableCell>
                      <TableCell align="center">Export/Print</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MODULES.map((module) => (
                      <TableRow key={module.key}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {module.label}
                          </Typography>
                        </TableCell>
                        {['view', 'edit', 'create', 'delete'].map((action) => (
                          <TableCell key={action} align="center">
                            {module.actions.includes(action) ? (
                              <Checkbox
                                size="small"
                                checked={permissions[module.key]?.[action] || false}
                                onChange={(e) => handlePermissionChange(module.key, action, e.target.checked)}
                                disabled={!useCustom}
                              />
                            ) : (
                              <Typography variant="body2" color="textSecondary">-</Typography>
                            )}
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          {module.actions.filter((a) => !['view', 'edit', 'create', 'delete'].includes(a)).map((action) => (
                            <FormControlLabel
                              key={action}
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={permissions[module.key]?.[action] || false}
                                  onChange={(e) => handlePermissionChange(module.key, action, e.target.checked)}
                                  disabled={!useCustom}
                                />
                              )}
                              label={ACTION_LABELS[action] || action}
                            />
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({ useCustom, matrix: permissions });
            } finally {
              setSaving(false);
            }
          }}
        >
          Save Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditGlobalPermissionsDialog;
